// `/admin/sellers` (`spec/17-frontend.md` §8.2). Foco declarado: **a tabela**. Sem
// step-up: `GET /admin/v1/sellers` não está em `ADMIN_STEP_UP_ROUTES`.
//
// Esta é a rota-PAI: ela desenha a lista e, por cima, o `RouteDrawer` com o `<Outlet/>`
// da rota `$id` (`spec/24` §1 decisão 5). Deep link em `.../sel_…` monta a lista embaixo
// e o drawer aberto; fechar volta para cá preservando o `search`.
//
// Só roteador: o schema de `search`, a navegação e o drawer. A tela é
// `features/admin-sellers/sellers-screen.tsx`.

import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import type { AdminSellerFilters } from '../api/queries/admin-sellers.ts';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { useSellerDrawerHeader } from '../features/admin-sellers/seller-detail.tsx';
import { SellersScreen } from '../features/admin-sellers/sellers-screen.tsx';

/**
 * `payout_locked` aceita boolean E a string `'true'`: o TanStack Router faz `JSON.parse`
 * do valor da query string (vira `true`), mas o item "Sinalizados" da sidebar navega por
 * um `href` cru (`/admin/sellers?payout_locked=true`) e nesse caminho o valor pode chegar
 * como texto. Aceitar os dois é uma linha; descobrir por que o filtro não pegou é uma
 * tarde.
 */
const searchSchema = z.object({
  status: z.enum(['pending_kyc', 'active', 'suspended', 'closed']).optional().catch(undefined),
  kyc_status: z.enum(['none', 'pending', 'approved', 'rejected']).optional().catch(undefined),
  q: z.string().min(1).optional().catch(undefined),
  payout_locked: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional()
    .catch(undefined)
    .transform((value) => (value === true || value === 'true' ? true : undefined)),
});

function AdminSellers() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const filters: AdminSellerFilters = search;
  // A rota `$id` é filha desta: se ela casa, o detalhe está aberto por cima da lista.
  const detail = useMatch({ from: '/admin/sellers/$id', shouldThrow: false });
  const header = useSellerDrawerHeader(detail?.params.id);

  const apply = (next: AdminSellerFilters): void => {
    void navigate({
      to: '/admin/sellers',
      search: {
        ...(next.status === undefined ? {} : { status: next.status }),
        ...(next.kyc_status === undefined ? {} : { kyc_status: next.kyc_status }),
        ...(next.q === undefined || next.q === '' ? {} : { q: next.q }),
        ...(next.payout_locked === true ? { payout_locked: true } : {}),
      },
    });
  };

  return (
    <>
      <SellersScreen
        filters={filters}
        onChange={apply}
        {...(detail === undefined ? {} : { selectedId: detail.params.id })}
        onSelect={(row) => {
          // `search` explícito: o filtro da lista tem de sobreviver à abertura do drawer,
          // e o `$id` herda o schema desta rota.
          void navigate({ to: '/admin/sellers/$id', params: { id: row.id }, search });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        width={460}
        onClose={() => {
          void navigate({ to: '/admin/sellers', search });
        }}
        title={header.title}
        sub={header.sub}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/sellers')({
  validateSearch: searchSchema,
  component: AdminSellers,
});
